import { Internal, FILE_REF_PROP, VAL_EXTENSION, FILE_REF_SUBTYPE_TAG } from '@valbuild/core';
import { Buffer } from 'buffer';
import { z } from 'zod';
import { fromZodError } from 'zod-validation-error';

function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}

function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}

function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: !0,
    configurable: !0,
    writable: !0
  }) : e[r] = t, e;
}

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}

function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}

function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0;
  }
}

function _createForOfIteratorHelper(r, e) {
  var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (!t) {
    if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) {
      t && (r = t);
      var n = 0,
        F = function () {};
      return {
        s: F,
        n: function () {
          return n >= r.length ? {
            done: !0
          } : {
            done: !1,
            value: r[n++]
          };
        },
        e: function (r) {
          throw r;
        },
        f: F
      };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }
  var o,
    a = !0,
    u = !1;
  return {
    s: function () {
      t = t.call(r);
    },
    n: function () {
      var r = t.next();
      return a = r.done, r;
    },
    e: function (r) {
      u = !0, o = r;
    },
    f: function () {
      try {
        a || null == t.return || t.return();
      } finally {
        if (u) throw o;
      }
    }
  };
}

function remirrorToRichTextSource(node, configDirectory, remoteOptions) {
  var files = {};
  var blocks = [];
  var i = 0;
  var _iterator = _createForOfIteratorHelper((node === null || node === void 0 ? void 0 : node.content) || []),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var child = _step.value;
      var block = convertBlock([(i++).toString()], child, files, configDirectory, remoteOptions);
      blocks.push(block);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  return {
    blocks: blocks,
    files: files
  };
}
function convertBlock(path, node, files, configDirectory, remoteOptions) {
  if (node.type === "heading") {
    var _node$attrs, _node$content;
    var depth = ((_node$attrs = node.attrs) === null || _node$attrs === void 0 ? void 0 : _node$attrs.level) || 1;
    if (Number.isNaN(depth)) {
      throw new Error("Invalid header depth");
    }
    return {
      tag: "h".concat(depth),
      children: ((_node$content = node.content) === null || _node$content === void 0 ? void 0 : _node$content.map(function (child, i) {
        return convertHeadingChild(path.concat("children", i.toString()), child, files, configDirectory, remoteOptions);
      })) || []
    };
  } else if (node.type === "paragraph") {
    return convertParagraph(path, node, files, configDirectory, remoteOptions);
  } else if (node.type === "bulletList") {
    return convertBulletList(path, node, files, configDirectory, remoteOptions);
  } else if (node.type === "orderedList") {
    return convertOrderedList(path, node, files, configDirectory, remoteOptions);
  } else {
    var exhaustiveCheck = node;
    throw new Error("Unhandled node type: ".concat("type" in exhaustiveCheck ? "exhaustiveCheck.type" : "unknown"));
  }
}
function convertHeadingChild(path, node, files, configDirectory, remoteOptions) {
  if (node.type === "text") {
    return convertTextNode(node);
  } else if (node.type === "hardBreak") {
    return {
      tag: "br"
    };
  } else if (node.type === "image") {
    return convertImageNode(path, node, files, configDirectory, remoteOptions);
  } else {
    var exhaustiveCheck = node;
    throw new Error("Unexpected heading child type: ".concat(JSON.stringify(exhaustiveCheck, null, 2)));
  }
}
function convertParagraph(path, child, files, configDirectory, remoteOptions) {
  var _child$content;
  return {
    tag: "p",
    children: ((_child$content = child.content) === null || _child$content === void 0 ? void 0 : _child$content.map(function (child, i) {
      if (child.type === "text") {
        return convertTextNode(child);
      } else if (child.type === "hardBreak") {
        return {
          tag: "br"
        };
      } else if (child.type === "image") {
        return convertImageNode(path.concat("children", i.toString()), child, files, configDirectory, remoteOptions);
      }
      var exhaustiveCheck = child;
      throw new Error("Unexpected paragraph child type: ".concat(JSON.stringify(exhaustiveCheck, null, 2)));
    })) || []
  };
}
function convertTextNode(node) {
  if (node.type === "text") {
    var _node$marks, _node$marks2;
    var styles = ((_node$marks = node.marks) === null || _node$marks === void 0 ? void 0 : _node$marks.flatMap(function (mark) {
      if (mark.type !== "link") {
        if (mark.type === "strike") {
          return ["line-through"];
        }
        return [mark.type];
      } else {
        return [];
      }
    })) || [];
    if ((_node$marks2 = node.marks) !== null && _node$marks2 !== void 0 && _node$marks2.some(function (mark) {
      return mark.type === "link";
    })) {
      var _node$marks$find2;
      if (styles.length > 0) {
        var _node$marks$find;
        return {
          tag: "a",
          href: ((_node$marks$find = node.marks.find(function (mark) {
            return mark.type === "link";
          })) === null || _node$marks$find === void 0 ? void 0 : _node$marks$find.attrs.href) || "",
          children: [{
            tag: "span",
            styles: styles,
            children: [node.text]
          }]
        };
      }
      return {
        tag: "a",
        href: ((_node$marks$find2 = node.marks.find(function (mark) {
          return mark.type === "link";
        })) === null || _node$marks$find2 === void 0 ? void 0 : _node$marks$find2.attrs.href) || "",
        children: [node.text]
      };
    }
    if (styles.length > 0) {
      return {
        tag: "span",
        styles: styles,
        children: [node.text]
      };
    }
    return node.text;
  } else {
    var exhaustiveCheck = node.type;
    throw new Error("Unexpected node type: ".concat(exhaustiveCheck));
  }
}
function convertListItem(path, child, files, configDirectory, remoteOptions) {
  var _child$content2;
  return {
    tag: "li",
    children: ((_child$content2 = child.content) === null || _child$content2 === void 0 ? void 0 : _child$content2.map(function (child, i) {
      if (child.type === "paragraph") {
        return convertParagraph(path.concat("children", i.toString()), child, files, configDirectory, remoteOptions);
      } else if (child.type === "bulletList") {
        return convertBulletList(path.concat("children", i.toString()), child, files, configDirectory, remoteOptions);
      } else if (child.type === "orderedList") {
        return convertOrderedList(path.concat("children", i.toString()), child, files, configDirectory, remoteOptions);
      } else {
        var exhaustiveCheck = child;
        throw new Error("Unexpected list child type: ".concat(exhaustiveCheck));
      }
    })) || []
  };
}
function convertBulletList(path, node, files, configDirectory, remoteOptions) {
  var _node$content2;
  return {
    tag: "ul",
    children: ((_node$content2 = node.content) === null || _node$content2 === void 0 ? void 0 : _node$content2.map(function (child, i) {
      if (child.type === "listItem") {
        return convertListItem(path.concat("children", i.toString()), child, files, configDirectory, remoteOptions);
      } else {
        var exhaustiveCheck = child.type;
        throw new Error("Unexpected bullet list child type: ".concat(exhaustiveCheck));
      }
    })) || []
  };
}
function convertOrderedList(path, node, files, configDirectory, remoteOptions) {
  var _node$content3;
  return {
    tag: "ol",
    children: ((_node$content3 = node.content) === null || _node$content3 === void 0 ? void 0 : _node$content3.map(function (child, i) {
      if (child.type === "listItem") {
        return convertListItem(path.concat("children", i.toString()), child, files, configDirectory, remoteOptions);
      } else {
        var exhaustiveCheck = child.type;
        throw new Error("Unexpected ordered list child type: ".concat(exhaustiveCheck));
      }
    })) || []
  };
}
var convertToNumber = function convertToNumber(value) {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "number") {
    return value;
  }
  var parsedValue = parseInt(value, 10);
  return isNaN(parsedValue) ? 0 : parsedValue;
};
var textEncoder = new TextEncoder();
function convertImageNode(path, node, files, configDirectory, remoteOptions) {
  if (node.attrs && node.attrs.src.startsWith("data:")) {
    var binaryData = Buffer.from(node.attrs.src.split(",")[1], "base64");
    var fullFileHash = Internal.getSHA256Hash(new Uint8Array(binaryData));
    var mimeType = Internal.getMimeType(node.attrs.src);
    if (mimeType === undefined) {
      throw new Error("Could not detect Mime Type for image: ".concat(node.attrs.src));
    }
    var fileName = Internal.createFilename(node.attrs.src, node.attrs.fileName || "", {
      width: typeof node.attrs.width === "number" ? node.attrs.width : 0,
      height: typeof node.attrs.height === "number" ? node.attrs.height : 0,
      mimeType: mimeType
    }, fullFileHash);
    var dir = configDirectory !== null && configDirectory !== void 0 && configDirectory.endsWith("/") ? configDirectory : "".concat(configDirectory, "/");
    var filePath = "".concat(dir).concat(fileName);
    var existingFilesEntry = files[filePath];
    var thisPath = path
    // file is added as src (see below):
    .concat("src");
    var remoteFileHash = Internal.remote.hashToRemoteFileHash(fullFileHash);
    var ref = remoteOptions ? Internal.remote.createRemoteRef(remoteOptions.remoteHost, _objectSpread2(_objectSpread2({}, remoteOptions), {}, {
      fileHash: remoteFileHash,
      validationHash: Internal.remote.getValidationHash(remoteOptions.coreVersion, remoteOptions.schema, mimeType, {
        width: typeof node.attrs.width === "number" ? node.attrs.width : 0,
        height: typeof node.attrs.height === "number" ? node.attrs.height : 0
      }, remoteFileHash, textEncoder),
      filePath: filePath.slice(1)
    })) : filePath;
    if (existingFilesEntry) {
      existingFilesEntry.patchPaths.push(thisPath);
    } else {
      // TODO: use ref instead on filePath?
      files[filePath] = {
        value: node.attrs.src,
        filePathOrRef: ref,
        metadata: {
          height: convertToNumber(node.attrs.height),
          width: convertToNumber(node.attrs.width),
          mimeType: mimeType
        },
        patchPaths: [thisPath]
      };
    }
    return {
      tag: "img",
      src: _objectSpread2(_objectSpread2(_defineProperty({}, FILE_REF_PROP, ref), remoteOptions ? _defineProperty({}, VAL_EXTENSION, "remote") : _defineProperty(_defineProperty({}, VAL_EXTENSION, "file"), FILE_REF_SUBTYPE_TAG, "image")), {}, {
        metadata: {
          width: typeof node.attrs.width === "number" ? node.attrs.width : 0,
          height: typeof node.attrs.height === "number" ? node.attrs.height : 0,
          mimeType: mimeType
        }
      })
    };
  } else if (node.attrs) {
    var url = node.attrs.src;
    var patchId = getParam("patch_id", url);
    var noParamsUrl = url.split("?")[0];
    var remote = false;
    if (patchId) {
      remote = getParam("remote", url) === "true";
      if (remote) {
        var remoteRef = getParam("ref", url);
        if (remoteRef) {
          noParamsUrl = decodeURIComponent(remoteRef);
        } else {
          console.error("Remote image URL does not contain ref: " + url);
        }
      } else if (noParamsUrl.startsWith("/api/val/files/public")) {
        noParamsUrl = noParamsUrl.slice("/api/val/files".length);
      } else {
        console.error("Patched image URL does not start with /api/val/files: " + url);
      }
    } else {
      if (remoteOptions && noParamsUrl.startsWith(remoteOptions.remoteHost)) {
        remote = true;
      } else if (!noParamsUrl.startsWith("/public")) {
        noParamsUrl = "/public".concat(noParamsUrl);
      } else {
        console.error("Unpatched image URL starts with /public: " + url);
      }
    }
    var tag = {
      tag: "img",
      src: _objectSpread2(_objectSpread2({}, remote ? _defineProperty(_defineProperty({}, VAL_EXTENSION, "remote"), FILE_REF_PROP, noParamsUrl) : _defineProperty(_defineProperty(_defineProperty({}, VAL_EXTENSION, "file"), FILE_REF_SUBTYPE_TAG, "image"), FILE_REF_PROP, noParamsUrl)), {}, {
        metadata: {
          width: typeof node.attrs.width === "number" ? node.attrs.width : 0,
          height: typeof node.attrs.height === "number" ? node.attrs.height : 0,
          mimeType: noParamsUrl && Internal.filenameToMimeType(noParamsUrl) || ""
        }
      }, patchId ? {
        patch_id: patchId
      } : {})
    };
    return tag;
  } else {
    throw new Error("Invalid image node (no attrs): " + JSON.stringify(node));
  }
}
function getParam(param, url) {
  var urlParts = url.split("?");
  if (urlParts.length < 2) {
    return undefined;
  }
  var queryString = urlParts[1];
  var params = new URLSearchParams(queryString);
  if (params.has(param)) {
    return params.get(param);
  }
  return undefined;
}

var RemirrorTextMark = z.object({
  type: z.union([z.literal("bold"), z.literal("strike"), z.literal("italic")])
});
var RemirrorLinkMark = z.object({
  type: z.literal("link"),
  attrs: z.object({
    href: z.string(),
    target: z.union([z.literal("_blank"), z["null"]()]),
    auto: z["boolean"]()
  })
});
var RemirrorText = z.intersection(z.object({
  type: z.literal("text"),
  text: z.string()
}), z.object({
  marks: z.array(z.union([RemirrorTextMark, RemirrorLinkMark]))
}).partial());
var RemirrorBr = z.intersection(z.object({
  type: z.literal("hardBreak")
}), z.object({
  marks: z.array(RemirrorTextMark)
}).partial());
var RemirrorImage = z.intersection(z.object({
  type: z.literal("image")
}), z.object({
  attrs: z.intersection(z.object({
    src: z.string()
  }), z.object({
    align: z.union([z.literal("center"), z.literal("end"), z.literal("justify"), z.literal("left"), z.literal("match-parent"), z.literal("right"), z.literal("start"), z["null"]()]),
    alt: z.union([z.string(), z["null"]()]),
    height: z.union([z.string(), z.number(), z["null"]()]),
    width: z.union([z.string(), z.number(), z["null"]()]),
    rotate: z.union([z.string(), z["null"]()]),
    title: z.union([z.string(), z["null"]()]),
    fileName: z.union([z.string(), z["null"]()])
  }).partial())
}).partial());
var RemirrorHeading = z.intersection(z.object({
  type: z.literal("heading")
}), z.object({
  attrs: z.object({
    level: z.number()
  }),
  content: z.array(z.union([RemirrorText, RemirrorImage, RemirrorBr]))
}).partial());
var RemirrorBulletList = z.lazy(function () {
  return z.intersection(z.object({
    type: z.literal("bulletList")
  }), z.object({
    content: z.array(RemirrorListItem)
  }).partial());
});
var RemirrorOrderedList = z.lazy(function () {
  return z.intersection(z.object({
    type: z.literal("orderedList")
  }), z.object({
    content: z.array(RemirrorListItem)
  }).partial());
});
var RemirrorListItem = z.lazy(function () {
  return z.intersection(z.object({
    type: z.literal("listItem")
  }), z.object({
    attrs: z.object({
      closed: z["boolean"](),
      nested: z["boolean"]()
    }).partial().optional(),
    content: z.array(z.union([RemirrorParagraph, RemirrorBulletList, RemirrorOrderedList]))
  }).partial());
});
var RemirrorParagraph = z.lazy(function () {
  return z.intersection(z.object({
    type: z.literal("paragraph")
  }), z.object({
    content: z.array(z.union([RemirrorText, RemirrorImage, RemirrorBr]))
  }).partial());
});
var RemirrorJSON = z.object({
  type: z.literal("doc"),
  content: z.array(z.union([RemirrorParagraph, RemirrorHeading, RemirrorBulletList, RemirrorOrderedList]))
});

function _arrayWithoutHoles(r) {
  if (Array.isArray(r)) return _arrayLikeToArray(r);
}

function _iterableToArray(r) {
  if ("undefined" != typeof Symbol && null != r[Symbol.iterator] || null != r["@@iterator"]) return Array.from(r);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(r) {
  return _arrayWithoutHoles(r) || _iterableToArray(r) || _unsupportedIterableToArray(r) || _nonIterableSpread();
}

function richTextToRemirror(richtext) {
  return {
    type: "doc",
    content: richtext.map(function (child) {
      return toRemirrorNode(child);
    })
  };
}
function toRemirrorNode(child) {
  switch (child.tag) {
    case "h1":
      return convertHeadingToRemirror(1, child.children);
    case "h2":
      return convertHeadingToRemirror(2, child.children);
    case "h3":
      return convertHeadingToRemirror(3, child.children);
    case "h4":
      return convertHeadingToRemirror(4, child.children);
    case "h5":
      return convertHeadingToRemirror(5, child.children);
    case "h6":
      return convertHeadingToRemirror(6, child.children);
    case "p":
      return convertParagraphToRemirror(child.children);
    case "ul":
      return convertUlToRemirror(child);
    case "ol":
      return convertOlToRemirror(child);
    default:
      {
        var _exhaustiveCheck = child;
        throw Error("Unexpected child node: " + JSON.stringify(_exhaustiveCheck));
      }
  }
}
function convertHeadingToRemirror(level, children) {
  return {
    type: "heading",
    attrs: {
      level: level
    },
    content: children.flatMap(convertInlineToRemirror)
  };
}
function convertStringToRemirror(child) {
  return {
    type: "text",
    text: child
  };
}
function convertInlineToRemirror(child) {
  if (typeof child === "string") {
    return [convertStringToRemirror(child)];
  }
  if (child.tag === "img") {
    return [convertImageNodeToRemirror(child)];
  }
  if (child.tag === "br") {
    return [createRemirrorBr()];
  }
  if (child.tag === "a") {
    return convertLinkNodeToRemirror(child);
  }
  return convertSpanNodeToRemirror(child);
}
function convertSpanNodeToRemirror(spanNode) {
  if (spanNode.styles.length === 0 && spanNode.children.length === 1) {
    if (typeof spanNode.children[0] === "string") {
      return [convertStringToRemirror(spanNode.children[0])];
    } else {
      // TODO: replace with error logging and something more graceful?
      throw Error("Unexpected amount of children in span node: " + JSON.stringify(spanNode, null, 2));
    }
  }
  var uniqueStyles = Array.from(new Set(spanNode.styles));
  return spanNode.children.map(function (child) {
    return {
      type: "text",
      text: child,
      marks: uniqueStyles.map(function (style) {
        switch (style) {
          case "bold":
            return {
              type: "bold"
            };
          case "italic":
            return {
              type: "italic"
            };
          case "line-through":
            return {
              type: "strike"
            };
          default:
            {
              var _exhaustiveCheck = style;
              throw Error("Unexpected span class: " + _exhaustiveCheck);
            }
        }
      })
    };
  });
}
function convertParagraphToRemirror(children) {
  return {
    type: "paragraph",
    content: children.flatMap(function (child) {
      if (typeof child === "string") {
        return [convertStringToRemirror(child)];
      }
      switch (child.tag) {
        case "span":
          return convertSpanNodeToRemirror(child);
        case "a":
          return convertLinkNodeToRemirror(child);
        case "img":
          return [convertImageNodeToRemirror(child)];
        case "br":
          return [createRemirrorBr()];
        default:
          {
            var _exhaustiveCheck = child;
            throw Error("Unexpected paragraph child: " + JSON.stringify(_exhaustiveCheck));
          }
      }
    })
  };
}
function createRemirrorBr() {
  return {
    type: "hardBreak",
    marks: []
  };
}
function convertLinkNodeToRemirror(linkNode) {
  return linkNode.children.flatMap(convertInlineToRemirror).map(function (remirrorNode) {
    if (remirrorNode.type !== "text") {
      return remirrorNode;
    }
    return _objectSpread2(_objectSpread2({}, remirrorNode), {}, {
      marks: (remirrorNode.marks || []).concat({
        type: "link",
        attrs: {
          href: linkNode.href,
          auto: false,
          target: null
        }
      })
    });
  });
}
function convertImageNodeToRemirror(imageNode) {
  var _fileSource$metadata, _fileSource$metadata2, _fileSource$metadata3;
  var fileSource = imageNode.src;
  if (!(VAL_EXTENSION in fileSource) || fileSource[VAL_EXTENSION] !== "file" && fileSource[VAL_EXTENSION] !== "remote") {
    throw Error("Expected file source in image node");
  }
  var fileVal;
  if (fileSource[VAL_EXTENSION] === "remote") {
    fileVal = Internal.convertRemoteSource(fileSource);
  } else {
    fileVal = Internal.convertFileSource(fileSource);
  }
  return {
    type: "image",
    attrs: {
      height: (_fileSource$metadata = fileSource.metadata) === null || _fileSource$metadata === void 0 ? void 0 : _fileSource$metadata.height,
      width: (_fileSource$metadata2 = fileSource.metadata) === null || _fileSource$metadata2 === void 0 ? void 0 : _fileSource$metadata2.width,
      alt: (_fileSource$metadata3 = fileSource.metadata) === null || _fileSource$metadata3 === void 0 ? void 0 : _fileSource$metadata3.alt,
      src: fileVal.url
    }
  };
}
function convertUlToRemirror(ulNode) {
  return {
    type: "bulletList",
    content: ulNode.children.map(convertListItemToRemirror)
  };
}
function convertOlToRemirror(olNode) {
  return {
    type: "orderedList",
    content: olNode.children.map(convertListItemToRemirror)
  };
}
function convertListItemToRemirror(liNode) {
  return {
    type: "listItem",
    content: convertListItemToRemirrorParagraph(liNode.children)
  };
}
function convertListItemToRemirrorParagraph(rtChildren) {
  var children = [];
  var _iterator = _createForOfIteratorHelper(rtChildren),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var child = _step.value;
      var lastChild = children[children.length - 1];
      if (!lastChild) {
        lastChild = {
          type: "paragraph",
          content: []
        };
        children.push(lastChild);
      }
      var lastChildContent = lastChild.content;
      if (typeof child === "string") {
        lastChildContent.push(convertStringToRemirror(child));
      } else {
        switch (child.tag) {
          case "ol":
            {
              children.push(convertOlToRemirror(child));
              break;
            }
          case "ul":
            {
              children.push(convertUlToRemirror(child));
              break;
            }
          case "p":
            {
              var newChild = convertParagraphToRemirror(child.children);
              if (lastChild.type === "paragraph" && newChild.type === "paragraph") {
                if (newChild.content) {
                  lastChildContent.push.apply(lastChildContent, _toConsumableArray(newChild.content));
                } else {
                  // no content - skip
                }
              } else {
                children.push(newChild);
              }
              break;
            }
          default:
            {
              var _exhaustiveCheck = child;
              throw Error("Unexpected list item child: " + JSON.stringify(_exhaustiveCheck));
            }
        }
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  return children;
}

var VAL_SESSION_COOKIE = Internal.VAL_SESSION_COOKIE;
var VAL_STATE_COOKIE = Internal.VAL_STATE_COOKIE;
var VAL_ENABLE_COOKIE_NAME = Internal.VAL_ENABLE_COOKIE_NAME;

function _regeneratorRuntime() {
  _regeneratorRuntime = function () {
    return e;
  };
  var t,
    e = {},
    r = Object.prototype,
    n = r.hasOwnProperty,
    o = Object.defineProperty || function (t, e, r) {
      t[e] = r.value;
    },
    i = "function" == typeof Symbol ? Symbol : {},
    a = i.iterator || "@@iterator",
    c = i.asyncIterator || "@@asyncIterator",
    u = i.toStringTag || "@@toStringTag";
  function define(t, e, r) {
    return Object.defineProperty(t, e, {
      value: r,
      enumerable: !0,
      configurable: !0,
      writable: !0
    }), t[e];
  }
  try {
    define({}, "");
  } catch (t) {
    define = function (t, e, r) {
      return t[e] = r;
    };
  }
  function wrap(t, e, r, n) {
    var i = e && e.prototype instanceof Generator ? e : Generator,
      a = Object.create(i.prototype),
      c = new Context(n || []);
    return o(a, "_invoke", {
      value: makeInvokeMethod(t, r, c)
    }), a;
  }
  function tryCatch(t, e, r) {
    try {
      return {
        type: "normal",
        arg: t.call(e, r)
      };
    } catch (t) {
      return {
        type: "throw",
        arg: t
      };
    }
  }
  e.wrap = wrap;
  var h = "suspendedStart",
    l = "suspendedYield",
    f = "executing",
    s = "completed",
    y = {};
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}
  var p = {};
  define(p, a, function () {
    return this;
  });
  var d = Object.getPrototypeOf,
    v = d && d(d(values([])));
  v && v !== r && n.call(v, a) && (p = v);
  var g = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(p);
  function defineIteratorMethods(t) {
    ["next", "throw", "return"].forEach(function (e) {
      define(t, e, function (t) {
        return this._invoke(e, t);
      });
    });
  }
  function AsyncIterator(t, e) {
    function invoke(r, o, i, a) {
      var c = tryCatch(t[r], t, o);
      if ("throw" !== c.type) {
        var u = c.arg,
          h = u.value;
        return h && "object" == typeof h && n.call(h, "__await") ? e.resolve(h.__await).then(function (t) {
          invoke("next", t, i, a);
        }, function (t) {
          invoke("throw", t, i, a);
        }) : e.resolve(h).then(function (t) {
          u.value = t, i(u);
        }, function (t) {
          return invoke("throw", t, i, a);
        });
      }
      a(c.arg);
    }
    var r;
    o(this, "_invoke", {
      value: function (t, n) {
        function callInvokeWithMethodAndArg() {
          return new e(function (e, r) {
            invoke(t, n, e, r);
          });
        }
        return r = r ? r.then(callInvokeWithMethodAndArg, callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg();
      }
    });
  }
  function makeInvokeMethod(e, r, n) {
    var o = h;
    return function (i, a) {
      if (o === f) throw Error("Generator is already running");
      if (o === s) {
        if ("throw" === i) throw a;
        return {
          value: t,
          done: !0
        };
      }
      for (n.method = i, n.arg = a;;) {
        var c = n.delegate;
        if (c) {
          var u = maybeInvokeDelegate(c, n);
          if (u) {
            if (u === y) continue;
            return u;
          }
        }
        if ("next" === n.method) n.sent = n._sent = n.arg;else if ("throw" === n.method) {
          if (o === h) throw o = s, n.arg;
          n.dispatchException(n.arg);
        } else "return" === n.method && n.abrupt("return", n.arg);
        o = f;
        var p = tryCatch(e, r, n);
        if ("normal" === p.type) {
          if (o = n.done ? s : l, p.arg === y) continue;
          return {
            value: p.arg,
            done: n.done
          };
        }
        "throw" === p.type && (o = s, n.method = "throw", n.arg = p.arg);
      }
    };
  }
  function maybeInvokeDelegate(e, r) {
    var n = r.method,
      o = e.iterator[n];
    if (o === t) return r.delegate = null, "throw" === n && e.iterator.return && (r.method = "return", r.arg = t, maybeInvokeDelegate(e, r), "throw" === r.method) || "return" !== n && (r.method = "throw", r.arg = new TypeError("The iterator does not provide a '" + n + "' method")), y;
    var i = tryCatch(o, e.iterator, r.arg);
    if ("throw" === i.type) return r.method = "throw", r.arg = i.arg, r.delegate = null, y;
    var a = i.arg;
    return a ? a.done ? (r[e.resultName] = a.value, r.next = e.nextLoc, "return" !== r.method && (r.method = "next", r.arg = t), r.delegate = null, y) : a : (r.method = "throw", r.arg = new TypeError("iterator result is not an object"), r.delegate = null, y);
  }
  function pushTryEntry(t) {
    var e = {
      tryLoc: t[0]
    };
    1 in t && (e.catchLoc = t[1]), 2 in t && (e.finallyLoc = t[2], e.afterLoc = t[3]), this.tryEntries.push(e);
  }
  function resetTryEntry(t) {
    var e = t.completion || {};
    e.type = "normal", delete e.arg, t.completion = e;
  }
  function Context(t) {
    this.tryEntries = [{
      tryLoc: "root"
    }], t.forEach(pushTryEntry, this), this.reset(!0);
  }
  function values(e) {
    if (e || "" === e) {
      var r = e[a];
      if (r) return r.call(e);
      if ("function" == typeof e.next) return e;
      if (!isNaN(e.length)) {
        var o = -1,
          i = function next() {
            for (; ++o < e.length;) if (n.call(e, o)) return next.value = e[o], next.done = !1, next;
            return next.value = t, next.done = !0, next;
          };
        return i.next = i;
      }
    }
    throw new TypeError(typeof e + " is not iterable");
  }
  return GeneratorFunction.prototype = GeneratorFunctionPrototype, o(g, "constructor", {
    value: GeneratorFunctionPrototype,
    configurable: !0
  }), o(GeneratorFunctionPrototype, "constructor", {
    value: GeneratorFunction,
    configurable: !0
  }), GeneratorFunction.displayName = define(GeneratorFunctionPrototype, u, "GeneratorFunction"), e.isGeneratorFunction = function (t) {
    var e = "function" == typeof t && t.constructor;
    return !!e && (e === GeneratorFunction || "GeneratorFunction" === (e.displayName || e.name));
  }, e.mark = function (t) {
    return Object.setPrototypeOf ? Object.setPrototypeOf(t, GeneratorFunctionPrototype) : (t.__proto__ = GeneratorFunctionPrototype, define(t, u, "GeneratorFunction")), t.prototype = Object.create(g), t;
  }, e.awrap = function (t) {
    return {
      __await: t
    };
  }, defineIteratorMethods(AsyncIterator.prototype), define(AsyncIterator.prototype, c, function () {
    return this;
  }), e.AsyncIterator = AsyncIterator, e.async = function (t, r, n, o, i) {
    void 0 === i && (i = Promise);
    var a = new AsyncIterator(wrap(t, r, n, o), i);
    return e.isGeneratorFunction(r) ? a : a.next().then(function (t) {
      return t.done ? t.value : a.next();
    });
  }, defineIteratorMethods(g), define(g, u, "Generator"), define(g, a, function () {
    return this;
  }), define(g, "toString", function () {
    return "[object Generator]";
  }), e.keys = function (t) {
    var e = Object(t),
      r = [];
    for (var n in e) r.push(n);
    return r.reverse(), function next() {
      for (; r.length;) {
        var t = r.pop();
        if (t in e) return next.value = t, next.done = !1, next;
      }
      return next.done = !0, next;
    };
  }, e.values = values, Context.prototype = {
    constructor: Context,
    reset: function (e) {
      if (this.prev = 0, this.next = 0, this.sent = this._sent = t, this.done = !1, this.delegate = null, this.method = "next", this.arg = t, this.tryEntries.forEach(resetTryEntry), !e) for (var r in this) "t" === r.charAt(0) && n.call(this, r) && !isNaN(+r.slice(1)) && (this[r] = t);
    },
    stop: function () {
      this.done = !0;
      var t = this.tryEntries[0].completion;
      if ("throw" === t.type) throw t.arg;
      return this.rval;
    },
    dispatchException: function (e) {
      if (this.done) throw e;
      var r = this;
      function handle(n, o) {
        return a.type = "throw", a.arg = e, r.next = n, o && (r.method = "next", r.arg = t), !!o;
      }
      for (var o = this.tryEntries.length - 1; o >= 0; --o) {
        var i = this.tryEntries[o],
          a = i.completion;
        if ("root" === i.tryLoc) return handle("end");
        if (i.tryLoc <= this.prev) {
          var c = n.call(i, "catchLoc"),
            u = n.call(i, "finallyLoc");
          if (c && u) {
            if (this.prev < i.catchLoc) return handle(i.catchLoc, !0);
            if (this.prev < i.finallyLoc) return handle(i.finallyLoc);
          } else if (c) {
            if (this.prev < i.catchLoc) return handle(i.catchLoc, !0);
          } else {
            if (!u) throw Error("try statement without catch or finally");
            if (this.prev < i.finallyLoc) return handle(i.finallyLoc);
          }
        }
      }
    },
    abrupt: function (t, e) {
      for (var r = this.tryEntries.length - 1; r >= 0; --r) {
        var o = this.tryEntries[r];
        if (o.tryLoc <= this.prev && n.call(o, "finallyLoc") && this.prev < o.finallyLoc) {
          var i = o;
          break;
        }
      }
      i && ("break" === t || "continue" === t) && i.tryLoc <= e && e <= i.finallyLoc && (i = null);
      var a = i ? i.completion : {};
      return a.type = t, a.arg = e, i ? (this.method = "next", this.next = i.finallyLoc, y) : this.complete(a);
    },
    complete: function (t, e) {
      if ("throw" === t.type) throw t.arg;
      return "break" === t.type || "continue" === t.type ? this.next = t.arg : "return" === t.type ? (this.rval = this.arg = t.arg, this.method = "return", this.next = "end") : "normal" === t.type && e && (this.next = e), y;
    },
    finish: function (t) {
      for (var e = this.tryEntries.length - 1; e >= 0; --e) {
        var r = this.tryEntries[e];
        if (r.finallyLoc === t) return this.complete(r.completion, r.afterLoc), resetTryEntry(r), y;
      }
    },
    catch: function (t) {
      for (var e = this.tryEntries.length - 1; e >= 0; --e) {
        var r = this.tryEntries[e];
        if (r.tryLoc === t) {
          var n = r.completion;
          if ("throw" === n.type) {
            var o = n.arg;
            resetTryEntry(r);
          }
          return o;
        }
      }
      throw Error("illegal catch attempt");
    },
    delegateYield: function (e, r, n) {
      return this.delegate = {
        iterator: values(e),
        resultName: r,
        nextLoc: n
      }, "next" === this.method && (this.arg = t), y;
    }
  }, e;
}

function asyncGeneratorStep(n, t, e, r, o, a, c) {
  try {
    var i = n[a](c),
      u = i.value;
  } catch (n) {
    return void e(n);
  }
  i.done ? t(u) : Promise.resolve(u).then(r, o);
}
function _asyncToGenerator(n) {
  return function () {
    var t = this,
      e = arguments;
    return new Promise(function (r, o) {
      var a = n.apply(t, e);
      function _next(n) {
        asyncGeneratorStep(a, r, o, _next, _throw, "next", n);
      }
      function _throw(n) {
        asyncGeneratorStep(a, r, o, _next, _throw, "throw", n);
      }
      _next(void 0);
    });
  };
}

var JSONValue = z.lazy(function () {
  return z.union([z.string(), z.number(), z["boolean"](), z["null"](), z.array(JSONValue), z.record(JSONValue)]);
});
var FileOperation = function FileOperation(path) {
  return z.object({
    op: z.literal("file"),
    path: path,
    filePath: z.string(),
    value: z.string(),
    remote: z["boolean"](),
    nestedFilePath: z.array(z.string()).optional(),
    metadata: JSONValue.optional() // TODO: remove optional
  });
};

/**
 * Raw JSON patch operation.
 */
var OperationJSON = z.discriminatedUnion("op", [z.object({
  op: z.literal("add"),
  path: z.string(),
  value: JSONValue
}).strict(), z.object({
  op: z.literal("remove"),
  /**
   * Must be non-root
   */
  path: z.string()
}).strict(), z.object({
  op: z.literal("replace"),
  path: z.string(),
  value: JSONValue
}).strict(), z.object({
  op: z.literal("move"),
  /**
   * Must be non-root and not a proper prefix of "path".
   */
  from: z.string(),
  path: z.string()
}).strict(), z.object({
  op: z.literal("copy"),
  from: z.string(),
  path: z.string()
}).strict(), z.object({
  op: z.literal("test"),
  path: z.string(),
  value: JSONValue
}).strict(), FileOperation(z.string()).strict()]);
var PatchJSON = z.array(OperationJSON);
/**
 * Raw JSON patch operation.
 */
var Operation = z.discriminatedUnion("op", [z.object({
  op: z.literal("add"),
  path: z.array(z.string()),
  value: JSONValue
}).strict(), z.object({
  op: z.literal("remove"),
  path: z.array(z.string()).nonempty()
}).strict(), z.object({
  op: z.literal("replace"),
  path: z.array(z.string()),
  value: JSONValue
}).strict(), z.object({
  op: z.literal("move"),
  from: z.array(z.string()).nonempty(),
  path: z.array(z.string())
}).strict(), z.object({
  op: z.literal("copy"),
  from: z.array(z.string()),
  path: z.array(z.string())
}).strict(), z.object({
  op: z.literal("test"),
  path: z.array(z.string()),
  value: JSONValue
}).strict(), FileOperation(z.array(z.string())).strict()]);
var Patch = z.array(Operation);
var PatchId = z.string().refine(function (_id) {
  return true;
} // TODO: validation
);
var ParentRef$1 = z.union([z.object({
  type: z.literal("head"),
  headBaseSha: z.string()
}), z.object({
  type: z.literal("patch"),
  patchId: PatchId
})]);
var PatchBlock = z.object({
  patch: Patch,
  parentRef: ParentRef$1
});

var SourcePath = z.string().transform(function (path) {
  return path;
});

var SerializedStringSchema = z.object({
  type: z.literal("string"),
  options: z.object({
    maxLength: z.number().optional(),
    minLength: z.number().optional(),
    regexp: z.object({
      source: z.string(),
      flags: z.string()
    }).optional()
  }).optional(),
  opt: z["boolean"](),
  raw: z["boolean"]()
});
var SerializedLiteralSchema = z.object({
  type: z.literal("literal"),
  value: z.string(),
  opt: z["boolean"]()
});
var SerializedBooleanSchema = z.object({
  type: z.literal("boolean"),
  opt: z["boolean"]()
});
var SerializedNumberSchema = z.object({
  type: z.literal("number"),
  options: z.object({
    max: z.number().optional(),
    min: z.number().optional()
  }).optional(),
  opt: z["boolean"]()
});
var SerializedObjectSchema = z.lazy(function () {
  return z.object({
    type: z.literal("object"),
    items: z.record(SerializedSchema),
    opt: z["boolean"]()
  });
});
var SerializedArraySchema = z.lazy(function () {
  return z.object({
    type: z.literal("array"),
    item: SerializedSchema,
    opt: z["boolean"]()
  });
});
var SerializedUnionSchema = z.lazy(function () {
  return z.union([z.object({
    type: z.literal("union"),
    key: SerializedLiteralSchema,
    items: z.array(SerializedLiteralSchema),
    opt: z["boolean"]()
  }), z.object({
    type: z.literal("union"),
    key: z.string(),
    items: z.array(SerializedObjectSchema),
    opt: z["boolean"]()
  })]);
});
var ImageOptions = z.object({
  ext: z.union([z.tuple([z.literal("jpg")]), z.tuple([z.literal("webp")])]).optional(),
  directory: z.string().optional(),
  prefix: z.string().optional(),
  accept: z.string().optional()
});
var SerializedImageSchema = z.object({
  type: z.literal("image"),
  options: ImageOptions.optional(),
  opt: z["boolean"]()
});
var RichTextOptions = z.object({
  style: z.object({
    bold: z["boolean"]().optional(),
    italic: z["boolean"]().optional(),
    lineThrough: z["boolean"]().optional()
  }).optional(),
  block: z.object({
    h1: z["boolean"]().optional(),
    h2: z["boolean"]().optional(),
    h3: z["boolean"]().optional(),
    h4: z["boolean"]().optional(),
    h5: z["boolean"]().optional(),
    h6: z["boolean"]().optional(),
    ul: z["boolean"]().optional(),
    ol: z["boolean"]().optional()
  }).optional(),
  inline: z.object({
    a: z["boolean"]().optional(),
    img: z.union([z["boolean"](), SerializedImageSchema]).optional()
  }).optional()
});
var SerializedRichTextSchema = z.object({
  type: z.literal("richtext"),
  options: RichTextOptions.optional(),
  opt: z["boolean"]()
});
var SerializedRecordSchema = z.lazy(function () {
  return z.object({
    type: z.literal("record"),
    item: SerializedSchema,
    opt: z["boolean"]()
  });
});
var SerializedKeyOfSchema = z.lazy(function () {
  return z.object({
    type: z.literal("keyOf"),
    path: SourcePath,
    schema: SerializedSchema,
    values: z.union([z.literal("string"), z.array(z.string())]),
    opt: z["boolean"]()
  });
});
var FileOptions = z.object({
  accept: z.string().optional()
});
var SerializedFileSchema = z.object({
  type: z.literal("file"),
  options: FileOptions.optional(),
  opt: z["boolean"]()
});
var SerializedDateSchema = z.object({
  type: z.literal("date"),
  opt: z["boolean"]()
});
var SerializedSchema = z.union([SerializedStringSchema, SerializedLiteralSchema, SerializedBooleanSchema, SerializedNumberSchema, SerializedObjectSchema, SerializedArraySchema, SerializedUnionSchema, SerializedRichTextSchema, SerializedRecordSchema, SerializedKeyOfSchema, SerializedFileSchema, SerializedDateSchema, SerializedImageSchema]);

var ValCommit = z.object({
  commitSha: z.string(),
  clientCommitSha: z.string(),
  parentCommitSha: z.string(),
  branch: z.string(),
  creator: z.string(),
  createdAt: z.string(),
  commitMessage: z.string().nullable()
});

var ModuleFilePath = z.string().refine(function (_path) {
  return true;
} // TODO: validation
);
var ParentRef = z.union([z.object({
  type: z.literal("head"),
  headBaseSha: z.string()
}), z.object({
  type: z.literal("patch"),
  patchId: PatchId
})]);
var ValConfig = z.object({
  project: z.string().optional(),
  root: z.string().optional(),
  files: z.object({
    directory: z.string() // TODO: validate that it is prefixed by /public/
  }).optional(),
  gitCommit: z.string().optional(),
  gitBranch: z.string().optional()
});
var ValidationFixZ = z.union([z.literal("image:change-extension"), z.literal("image:add-metadata"), z.literal("image:check-metadata"), z.literal("image:check-remote"), z.literal("image:upload-remote"), z.literal("image:download-remote"), z.literal("file:change-extension"), z.literal("file:add-metadata"), z.literal("file:check-metadata"), z.literal("file:check-remote"), z.literal("file:upload-remote"), z.literal("file:download-remote"), z.literal("keyof:check-keys")]);
var ValidationError = z.object({
  message: z.string(),
  value: z.unknown().optional(),
  fatal: z["boolean"]().optional(),
  fixes: z.array(ValidationFixZ).optional()
});
var unauthorizedResponse = z.object({
  status: z.literal(401),
  json: z.object({
    message: z.string()
  })
});
var notFoundResponse = z.object({
  status: z.literal(404),
  json: z.object({
    message: z.string()
  })
});
var GenericError = z.object({
  message: z.string()
});
var GenericPatchError = z.union([z.object({
  patchId: PatchId,
  message: z.string()
}), z.object({
  parentPatchId: z.string(),
  message: z.string()
})]);
var ModulesError = z.object({
  message: z.string(),
  path: ModuleFilePath.optional()
});
z.union([z.literal("val_session"), z.literal("val_enable"), z.literal("val_state")]);
var enableCookieValue = z.object({
  value: z.literal("true"),
  options: z.object({
    httpOnly: z.literal(false),
    sameSite: z.literal("lax")
  })
});
var Api = {
  "/draft/enable": {
    GET: {
      req: {
        query: {
          redirect_to: z.string().optional()
        },
        cookies: _defineProperty({}, VAL_SESSION_COOKIE, z.string().optional())
      },
      res: z.union([z.object({
        status: z.literal(401),
        json: GenericError
      }), z.object({
        status: z.literal(302),
        redirectTo: z.string()
      }), z.object({
        status: z.literal(400),
        json: z.object({
          message: z.string()
        })
      })])
    }
  },
  "/draft/disable": {
    GET: {
      req: {
        query: {
          redirect_to: z.string().optional()
        },
        cookies: _defineProperty({}, VAL_SESSION_COOKIE, z.string().optional())
      },
      res: z.union([z.object({
        status: z.literal(401),
        json: GenericError
      }), z.object({
        status: z.literal(302),
        redirectTo: z.string()
      }), z.object({
        status: z.literal(400),
        json: z.object({
          message: z.string()
        })
      })])
    }
  },
  "/draft/stat": {
    GET: {
      req: {
        cookies: _defineProperty({}, VAL_SESSION_COOKIE, z.string().optional())
      },
      res: z.union([z.object({
        status: z.literal(401),
        json: GenericError
      }), z.object({
        status: z.literal(200),
        json: z.object({
          draftMode: z["boolean"]()
        })
      })])
    }
  },
  "/enable": {
    GET: {
      req: {
        query: {
          redirect_to: z.string().optional()
        },
        cookies: _defineProperty({}, VAL_SESSION_COOKIE, z.string().optional())
      },
      res: z.union([z.object({
        status: z.literal(401),
        json: GenericError
      }), z.object({
        status: z.literal(302),
        redirectTo: z.string(),
        cookies: z.object(_defineProperty(_defineProperty({}, VAL_ENABLE_COOKIE_NAME, enableCookieValue), VAL_STATE_COOKIE, z.object({
          value: z.string(),
          options: z.object({
            httpOnly: z.literal(true),
            sameSite: z.literal("lax"),
            expires: z["instanceof"](Date)
          })
        })))
      }), z.object({
        status: z.literal(302),
        redirectTo: z.string(),
        cookies: z.object(_defineProperty({}, VAL_ENABLE_COOKIE_NAME, enableCookieValue))
      }), z.object({
        status: z.literal(400),
        json: z.object({
          message: z.string()
        })
      })])
    }
  },
  "/disable": {
    GET: {
      req: {
        query: {
          redirect_to: z.string().optional()
        },
        cookies: _defineProperty({}, VAL_SESSION_COOKIE, z.string().optional())
      },
      res: z.union([z.object({
        status: z.literal(401),
        json: GenericError
      }), z.object({
        status: z.literal(302),
        redirectTo: z.string(),
        cookies: z.object(_defineProperty({}, VAL_ENABLE_COOKIE_NAME, z.object({
          value: z.literal("false")
        })))
      }), z.object({
        status: z.literal(400),
        json: z.object({
          message: z.string()
        })
      })])
    }
  },
  "/authorize": {
    GET: {
      req: {
        query: {
          redirect_to: z.string().optional()
        }
      },
      res: z.union([z.object({
        status: z.literal(302),
        redirectTo: z.string(),
        cookies: z.object(_defineProperty(_defineProperty({}, VAL_ENABLE_COOKIE_NAME, enableCookieValue), VAL_STATE_COOKIE, z.object({
          value: z.string(),
          options: z.object({
            httpOnly: z.literal(true),
            sameSite: z.literal("lax"),
            expires: z["instanceof"](Date)
          })
        })))
      }), z.object({
        status: z.literal(400),
        json: z.object({
          message: z.string()
        })
      })])
    }
  },
  "/callback": {
    GET: {
      req: {
        query: {
          code: z.string().optional(),
          state: z.string().optional()
        },
        cookies: _defineProperty({}, VAL_STATE_COOKIE, z.string())
      },
      res: z.object({
        status: z.literal(302),
        redirectTo: z.string(),
        cookies: z.object(_defineProperty(_defineProperty(_defineProperty({}, VAL_STATE_COOKIE, z.object({
          value: z.literal(null)
        })), VAL_ENABLE_COOKIE_NAME, enableCookieValue.optional()), VAL_SESSION_COOKIE, z.object({
          value: z.string(),
          options: z.object({
            httpOnly: z.literal(true),
            sameSite: z.literal("strict"),
            path: z.string(),
            secure: z.literal(true),
            expires: z["instanceof"](Date)
          }).optional()
        }).optional()))
      })
    }
  },
  "/session": {
    GET: {
      req: {
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([z.object({
        status: z.literal(200),
        json: z.object({
          mode: z.union([z.literal("local"), z.literal("proxy")]),
          enabled: z["boolean"]()
        })
      }), z.object({
        status: z.union([
        // TODO: Remove the ones we don't need.
        z.literal(400), z.literal(401), z.literal(403), z.literal(404), z.literal(500), z.literal(501)]),
        json: z.object({
          message: z.string()
        })
      }), z.object({
        status: z.literal(401),
        json: z.object({
          message: z.string(),
          details: z.union([z.string(), z.object({
            reason: z.string()
          }), z.object({
            sub: z.string(),
            exp: z.number(),
            token: z.string(),
            org: z.string(),
            project: z.string()
          })])
        })
      }), z.object({
        status: z.literal(500),
        json: z.object({
          message: z.string()
        })
      })])
    }
  },
  "/logout": {
    GET: {
      req: {
        query: {
          redirect_to: z.string().optional()
        }
      },
      // TODO fix req types
      res: z.union([z.object({
        status: z.literal(200),
        cookies: z.object(_defineProperty(_defineProperty({}, VAL_SESSION_COOKIE, z.object({
          value: z.string(),
          options: z.object({
            httpOnly: z.literal(true),
            sameSite: z.literal("strict"),
            path: z.string(),
            secure: z.literal(true),
            expires: z["instanceof"](Date)
          }).optional()
        }).optional()), VAL_STATE_COOKIE, z.object({
          value: z.string(),
          options: z.object({
            httpOnly: z.literal(true),
            sameSite: z.literal("strict"),
            path: z.string(),
            secure: z.literal(true),
            expires: z["instanceof"](Date)
          }).optional()
        }).optional()))
      }), z.object({
        status: z.literal(302),
        redirectTo: z.string(),
        cookies: z.object(_defineProperty(_defineProperty({}, VAL_SESSION_COOKIE, z.object({
          value: z.string(),
          options: z.object({
            httpOnly: z.literal(true),
            sameSite: z.literal("strict"),
            path: z.string(),
            secure: z.literal(true),
            expires: z["instanceof"](Date)
          }).optional()
        }).optional()), VAL_STATE_COOKIE, z.object({
          value: z.string(),
          options: z.object({
            httpOnly: z.literal(true),
            sameSite: z.literal("strict"),
            path: z.string(),
            secure: z.literal(true),
            expires: z["instanceof"](Date)
          }).optional()
        }).optional()))
      })])
    }
  },
  "/remote/settings": {
    GET: {
      req: {
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([z.object({
        status: z.literal(200),
        json: z.object({
          publicProjectId: z.string(),
          coreVersion: z.string(),
          remoteFileBuckets: z.array(z.object({
            bucket: z.string()
          }))
        })
      }), z.object({
        status: z.literal(400),
        json: z.object({
          errorCode: z.union([z.literal("project-not-configured"), z.literal("error-could-not-get-settings"), z.literal("project-not-configured"), z.literal("pat-error"), z.literal("api-key-missing")]),
          message: z.string()
        })
      }), z.object({
        status: z.literal(401),
        json: z.object({
          errorCode: z.literal("unauthorized").optional(),
          message: z.string()
        })
      }), z.object({
        status: z.literal(500),
        json: z.object({
          message: z.string()
        })
      })])
    }
  },
  "/stat": {
    POST: {
      req: {
        body: z.object({
          sourcesSha: z.string(),
          schemaSha: z.string(),
          baseSha: z.string(),
          patches: z.array(z.string()).optional()
        }).nullable(),
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([z.object({
        status: z.literal(401),
        json: GenericError
      }), z.object({
        status: z.literal(500),
        json: GenericError
      }), z.object({
        status: z.literal(503),
        json: GenericError
      }), z.object({
        status: z.literal(200),
        json: z.union([z.object({
          type: z.union([z.literal("request-again"), z.literal("no-change"), z.literal("did-change")]),
          baseSha: z.string(),
          schemaSha: z.string(),
          sourcesSha: z.string(),
          patches: z.array(PatchId),
          config: ValConfig,
          profileId: z.string().nullable(),
          mode: z.union([z.literal("http"), z.literal("fs")])
        }), z.object({
          type: z.literal("use-websocket"),
          url: z.string(),
          nonce: z.string(),
          baseSha: z.string(),
          schemaSha: z.string(),
          sourcesSha: z.string(),
          commitSha: z.string(),
          patches: z.array(PatchId),
          commits: z.array(ValCommit),
          config: ValConfig,
          profileId: z.string().nullable(),
          mode: z.union([z.literal("http"), z.literal("fs")])
        })])
      })])
    }
  },
  // This has a path which is like this: /upload/patches/:patchId/files. Example: /upload/patches/76b9237a-7712-4d60-88b4-d273e6d6fe18/files
  "/upload/patches": {
    POST: {
      req: {
        path: z.string().optional(),
        body: z.object({
          parentRef: ParentRef,
          filePath: z.string(),
          data: z.any(),
          // TODO: Json zod type
          type: z.union([z.literal("file"), z.literal("image")]),
          metadata: z.any(),
          // TODO: Json zod type
          remote: z["boolean"]()
        })
      },
      res: z.union([z.object({
        status: z.literal(400),
        json: GenericError
      }), z.object({
        status: z.literal(200),
        json: z.object({
          filePath: z.string(),
          patchId: PatchId
        })
      })])
    }
  },
  "/direct-file-upload-settings": {
    POST: {
      req: {
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([z.object({
        status: z.literal(400),
        json: GenericError
      }), z.object({
        status: z.literal(401),
        json: GenericError
      }), z.object({
        status: z.literal(500),
        json: GenericError
      }), z.object({
        status: z.literal(200),
        json: z.object({
          nonce: z.string().nullable(),
          baseUrl: z.string()
        })
      })])
    }
  },
  "/patches": {
    DELETE: {
      req: {
        query: {
          id: z.array(PatchId)
        },
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([unauthorizedResponse, z.object({
        status: z.literal(500),
        json: z.object({
          message: z.string(),
          errors: z.array(GenericPatchError)
        })
      }), z.object({
        status: z.literal(200),
        json: z.array(PatchId)
      })])
    },
    PUT: {
      req: {
        body: z.object({
          parentRef: ParentRef,
          patches: z.array(z.object({
            path: ModuleFilePath,
            patchId: PatchId,
            patch: z.any() // TODO: this should be Patch instead - we got a weird validation error: although input looks good, it still does not accept objects as values... Which it should do via the z.record(JSONValue) type
          }))
        }),
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([unauthorizedResponse, z.object({
        status: z.literal(409),
        // conflict: i.e. not a head of patches
        json: z.object({
          type: z.literal("patch-head-conflict"),
          message: z.string()
        })
      }), z.object({
        status: z.literal(400),
        json: z.object({
          type: z.literal("patch-error"),
          message: z.string(),
          errors: z.record(ModuleFilePath, z.array(z.object({
            error: GenericError
          })))
        })
      }), z.object({
        status: z.literal(200),
        json: z.object({
          newPatchIds: z.array(PatchId),
          parentRef: ParentRef
        })
      })])
    },
    GET: {
      req: {
        query: {
          patch_id: z.array(PatchId).optional(),
          exclude_patch_ops: z["boolean"]().optional()
        },
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([unauthorizedResponse, z.object({
        status: z.literal(500),
        json: z.object({
          message: z.string(),
          patchErrors: z.array(GenericPatchError)
        })
      }), z.object({
        status: z.literal(500),
        json: z.object({
          message: z.string(),
          error: GenericError
        })
      }), z.object({
        status: z.literal(200),
        json: z.object({
          patches: z.array(z.object({
            path: ModuleFilePath,
            patch: Patch.optional(),
            patchId: PatchId,
            createdAt: z.string(),
            authorId: z.string().nullable(),
            appliedAt: z.object({
              commitSha: z.string()
            }).nullable()
          })),
          baseSha: z.string(),
          error: GenericError.optional(),
          errors: z.record(PatchId, GenericError).optional()
        })
      })])
    }
  },
  "/schema": {
    GET: {
      req: {
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([unauthorizedResponse, z.object({
        status: z.literal(500),
        json: z.object({
          message: z.string(),
          details: z.array(ModulesError)
        })
      }), z.object({
        status: z.literal(500),
        json: z.object({
          message: z.string(),
          details: z.array(GenericError)
        })
      }), z.object({
        status: z.literal(200),
        json: z.object({
          schemaSha: z.string(),
          schemas: z.record(ModuleFilePath, SerializedSchema)
        })
      })])
    }
  },
  "/sources/~": {
    PUT: {
      req: {
        path: z.string().optional(),
        query: {
          validate_sources: z["boolean"]().optional(),
          validate_binary_files: z["boolean"]().optional()
        },
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([unauthorizedResponse, z.object({
        status: z.literal(401),
        json: GenericError
      }), z.object({
        status: z.literal(500),
        json: z.object({
          message: z.string(),
          details: z.union([z.array(ModulesError), GenericError])
        })
      }), z.object({
        status: z.literal(409),
        json: z.object({
          message: z.string()
        })
      }), z.object({
        status: z.literal(400),
        json: z.object({
          message: z.string(),
          details: z.array(ModulesError)
        })
      }), z.object({
        status: z.literal(200),
        json: z.object({
          schemaSha: z.string(),
          sourcesSha: z.string(),
          modules: z.record(ModuleFilePath, z.object({
            preview: z.any(),
            // TODO: improve this type
            source: z.any(),
            //.optional(), // TODO: Json zod type
            patches: z.object({
              applied: z.array(PatchId),
              skipped: z.array(PatchId).optional(),
              errors: z.record(PatchId, GenericError).optional()
            }).optional(),
            validationErrors: z.record(z.string(), z.array(ValidationError)).optional()
          }))
        })
      })])
    }
  },
  "/profiles": {
    GET: {
      req: {
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([unauthorizedResponse, z.object({
        status: z.literal(200),
        json: z.object({
          profiles: z.array(z.object({
            profileId: z.string(),
            fullName: z.string(),
            email: z.string().optional(),
            avatar: z.object({
              url: z.string()
            }).nullable()
          }))
        })
      })])
    }
  },
  "/commit-summary": {
    GET: {
      req: {
        query: {
          patch_id: z.array(PatchId)
        },
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([unauthorizedResponse, z.object({
        status: z.literal(400),
        json: z.object({
          message: z.string()
        })
      }), z.object({
        status: z.literal(200),
        json: z.object({
          patchIds: z.array(PatchId),
          baseSha: z.string(),
          commitSummary: z.string().nullable()
        })
      })])
    }
  },
  "/save": {
    POST: {
      req: {
        body: z.object({
          message: z.string().optional(),
          patchIds: z.array(PatchId)
        }),
        cookies: {
          val_session: z.string().optional()
        }
      },
      res: z.union([unauthorizedResponse, z.object({
        status: z.literal(200),
        json: z.object({}) // TODO:
      }), z.object({
        status: z.literal(409),
        json: z.object({
          message: z.string(),
          isNotFastForward: z.literal(true)
        })
      }), z.object({
        status: z.literal(400),
        json: z.union([z.object({
          message: z.string(),
          details: z.union([z.object({
            sourceFilePatchErrors: z.record(ModuleFilePath, z.array(GenericError)),
            binaryFilePatchErrors: z.record(GenericError)
          }), z.array(GenericError)]).optional()
        }), z.object({
          message: z.string(),
          errorCode: z.union([z.literal("project-not-configured"), z.literal("pat-error")])
        })])
      })])
    }
  },
  "/files": {
    GET: {
      req: {
        path: z.string(),
        query: {
          patch_id: PatchId.optional(),
          remote: z.string().optional()
        }
      },
      res: z.union([unauthorizedResponse, notFoundResponse, z.object({
        status: z.literal(200),
        body: z["instanceof"](ReadableStream)
      })])
    }
  }
};

// Types and helper types:

/**
 * Extracts the keys of an object where the value is not undefined.
 */

/**
 * Extracts the keys of an object where the value is not undefined.
 * Then picks the keys from the object.
 * This is useful for creating a new object type with only the defined keys.
 * @example
 * type A = { a: string; b?: number };
 * type B = DefinedObject<A>; // { a: string }
 */

/**
 * This schema supports:
 * 1. multiple query params with the same name
 * 2. simple API route definitions where:
 *  2.1. z.array means at least one query params this name is required
 *  2.2. z.optional means no query param of this name is accepted
 *  2.3. z.array(...).optional() means zero or more query params of this name is accepted
 *
 * Do not change this without updating the ValRouter query parsing logic
 * */

var createValClient = function createValClient(host, config // We want to use this in the future
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  var anyApi = Api;
  return /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee2(path, method, req) {
      var _anyApi$path, _apiEndpoint$req$body;
      var anyReq, fullPath, params, _i, _Object$keys, _anyReq$query, key, _anyReq$query2, _iterator, _step, value, _anyReq$query3, _anyReq$query4, apiEndpoint, reqBodyResult, res;
      return _regeneratorRuntime().wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            anyReq = req;
            fullPath = path;
            if (anyReq !== null && anyReq !== void 0 && anyReq.path && anyReq.path.length > 0) {
              fullPath += anyReq.path;
            }
            if (anyReq.query) {
              params = [];
              for (_i = 0, _Object$keys = Object.keys(anyReq.query); _i < _Object$keys.length; _i++) {
                key = _Object$keys[_i];
                if (Array.isArray((_anyReq$query = anyReq.query) === null || _anyReq$query === void 0 ? void 0 : _anyReq$query[key])) {
                  _iterator = _createForOfIteratorHelper(((_anyReq$query2 = anyReq.query) === null || _anyReq$query2 === void 0 ? void 0 : _anyReq$query2[key]) || []);
                  try {
                    for (_iterator.s(); !(_step = _iterator.n()).done;) {
                      value = _step.value;
                      if (value !== undefined) {
                        params.push([key, value.toString()]);
                      }
                    }
                  } catch (err) {
                    _iterator.e(err);
                  } finally {
                    _iterator.f();
                  }
                } else {
                  if (((_anyReq$query3 = anyReq.query) === null || _anyReq$query3 === void 0 ? void 0 : _anyReq$query3[key]) !== undefined) {
                    params.push([key, anyReq === null || anyReq === void 0 || (_anyReq$query4 = anyReq.query) === null || _anyReq$query4 === void 0 ? void 0 : _anyReq$query4[key].toString()]);
                  }
                }
              }
              if (anyReq !== null && anyReq !== void 0 && anyReq.query && params.length > 0) {
                fullPath += "?".concat(new URLSearchParams(params).toString());
              }
            }
            apiEndpoint = anyApi === null || anyApi === void 0 || (_anyApi$path = anyApi[path]) === null || _anyApi$path === void 0 ? void 0 : _anyApi$path[method];
            if (apiEndpoint) {
              _context2.next = 7;
              break;
            }
            return _context2.abrupt("return", {
              status: null,
              json: {
                message: "Invalid route ".concat(path, " with method ").concat(method, ". This is most likely a Val bug."),
                type: "client_side_validation_error",
                details: {
                  validationError: "Route ".concat(path, " with method ").concat(method, " not found."),
                  data: {
                    routes: Object.keys(anyApi || {}),
                    methods: Object.keys((anyApi === null || anyApi === void 0 ? void 0 : anyApi[path]) || {}),
                    route: path,
                    method: method
                  }
                }
              }
            });
          case 7:
            reqBodyResult = (_apiEndpoint$req$body = apiEndpoint.req.body) === null || _apiEndpoint$req$body === void 0 ? void 0 : _apiEndpoint$req$body.safeParse(anyReq.body);
            if (!(reqBodyResult && !reqBodyResult.success)) {
              _context2.next = 11;
              break;
            }
            console.error("Got an invalid request body while validating client-side. This is most likely a Val bug.", {
              path: path,
              method: method,
              body: anyReq.body,
              error: reqBodyResult.error
            });
            return _context2.abrupt("return", {
              status: null,
              json: {
                message: "There was an issue validating your data. This is most likely a Val bug.",
                type: "client_side_validation_error",
                details: {
                  validationError: fromZodError(reqBodyResult.error).toString(),
                  data: anyReq.body
                }
              }
            });
          case 11:
            _context2.prev = 11;
            _context2.next = 14;
            return fetch("".concat(host).concat(fullPath), {
              method: method,
              headers: {
                "Content-Type": "application/json"
              },
              body: anyReq.body !== undefined ? JSON.stringify(anyReq.body) : undefined
            }).then(/*#__PURE__*/function () {
              var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime().mark(function _callee(res) {
                var _apiEndpoint$res;
                var contentTypeHeaders, json, valClientResult, responseResult;
                return _regeneratorRuntime().wrap(function _callee$(_context) {
                  while (1) switch (_context.prev = _context.next) {
                    case 0:
                      contentTypeHeaders = res.headers.get("content-type");
                      if (!(res.status === 413)) {
                        _context.next = 3;
                        break;
                      }
                      return _context.abrupt("return", {
                        status: 413,
                        json: {
                          message: "Request too large.",
                          method: method,
                          path: path
                        }
                      });
                    case 3:
                      if (contentTypeHeaders !== null && contentTypeHeaders !== void 0 && contentTypeHeaders.includes("application/json")) {
                        _context.next = 5;
                        break;
                      }
                      return _context.abrupt("return", {
                        status: null,
                        json: {
                          type: "client_side_validation_error",
                          message: "Invalid response (expected JSON, but got something else). This could be a result of mismatched Val versions or a transient error. ".concat(res.status),
                          details: {
                            validationError: "Invalid content type",
                            data: {
                              status: res.status,
                              contentType: contentTypeHeaders
                            }
                          }
                        }
                      });
                    case 5:
                      _context.next = 7;
                      return res.json();
                    case 7:
                      json = _context.sent;
                      valClientResult = {
                        status: res.status,
                        json: json
                      };
                      if (!(res.status === 500)) {
                        _context.next = 12;
                        break;
                      }
                      console.error("Server responded with an error", json);
                      return _context.abrupt("return", {
                        status: 500,
                        json: _objectSpread2({
                          type: "unknown",
                          message: json.message
                        }, json)
                      });
                    case 12:
                      responseResult = (_apiEndpoint$res = apiEndpoint.res) === null || _apiEndpoint$res === void 0 ? void 0 : _apiEndpoint$res.safeParse(valClientResult);
                      if (!(responseResult && !responseResult.success)) {
                        _context.next = 15;
                        break;
                      }
                      return _context.abrupt("return", {
                        status: null,
                        json: {
                          message: "Response could not be validated. This could also be a result of mismatched Val versions.",
                          type: "client_side_validation_error",
                          details: {
                            validationError: fromZodError(responseResult.error).toString(),
                            data: valClientResult
                          }
                        }
                      });
                    case 15:
                      return _context.abrupt("return", {
                        status: res.status,
                        json: json
                      });
                    case 16:
                    case "end":
                      return _context.stop();
                  }
                }, _callee);
              }));
              return function (_x4) {
                return _ref2.apply(this, arguments);
              };
            }());
          case 14:
            res = _context2.sent;
            return _context2.abrupt("return", res);
          case 18:
            _context2.prev = 18;
            _context2.t0 = _context2["catch"](11);
            return _context2.abrupt("return", {
              status: null,
              json: {
                message: "Failed to fetch data",
                type: "network_error",
                retryable: isRetryable(_context2.t0),
                details: _context2.t0 instanceof Error ? _context2.t0.message : JSON.stringify(_context2.t0)
              }
            });
          case 21:
          case "end":
            return _context2.stop();
        }
      }, _callee2, null, [[11, 18]]);
    }));
    return function (_x, _x2, _x3) {
      return _ref.apply(this, arguments);
    };
  }();
};
function isRetryable(error) {
  if (error instanceof TypeError) {
    // TypeError is usually thrown by fetch when the network request fails.
    return true;
  }
  if (error instanceof Error && "code" in error) {
    var errorCode = error.code;

    // Network-specific errors (Node.js specific)
    var retryableErrorCodes = ["ECONNRESET", "ETIMEDOUT", "ENOTFOUND", "EAI_AGAIN"];
    return retryableErrorCodes.includes(errorCode);
  }
  return false;
}

function _arrayWithHoles(r) {
  if (Array.isArray(r)) return r;
}

function _iterableToArrayLimit(r, l) {
  var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = !0,
      o = !1;
    try {
      if (i = (t = t.call(r)).next, 0 === l) {
        if (Object(t) !== t) return;
        f = !1;
      } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0);
    } catch (r) {
      o = !0, n = r;
    } finally {
      try {
        if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(r, e) {
  return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest();
}

var urlOf = function urlOf() {
  var route = arguments.length <= 0 ? undefined : arguments[0];
  var query = arguments.length <= 1 ? undefined : arguments[1];
  if (query) {
    var params = Object.entries(query).flatMap(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 2),
        key = _ref2[0],
        value = _ref2[1];
      if (!value) {
        return [];
      }
      return [[key, value.toString()]];
    });
    var searchParams = new URLSearchParams(params);
    return "".concat(route + "?" + searchParams.toString());
  }
  return "".concat(route);
};

var VAL_CONFIG_LISTENER = "val-config";
var VAL_CONFIG_RECEIVED_LISTENER = "val-config-received";

var SharedValConfig = z.object({
  contentHostUrl: z.string().optional(),
  appHostUrl: z.string().optional(),
  project: z.string().optional(),
  root: z.string().optional(),
  files: z.object({
    directory: z.literal("/public/val")
  }).optional(),
  gitCommit: z.string().optional(),
  gitBranch: z.string().optional(),
  defaultTheme: z["enum"](["dark", "light"]).optional(),
  ai: z.object({
    commitMessages: z.object({
      disabled: z["boolean"]().optional()
    }).optional()
  }).optional()
});

/**
 * Config that is shared between the client and server.
 */

var ValDeployment = z.object({
  deploymentId: z.string(),
  deploymentState: z.string(),
  commitSha: z.string(),
  updatedAt: z.string(),
  createdAt: z.string()
});

export { Api, ParentRef$1 as ParentRef, Patch, PatchBlock, PatchId, PatchJSON, RemirrorBr, RemirrorBulletList, RemirrorHeading, RemirrorImage, RemirrorJSON, RemirrorLinkMark, RemirrorListItem, RemirrorOrderedList, RemirrorParagraph, RemirrorText, RemirrorTextMark, SharedValConfig, VAL_CONFIG_LISTENER, VAL_CONFIG_RECEIVED_LISTENER, VAL_ENABLE_COOKIE_NAME, VAL_SESSION_COOKIE, VAL_STATE_COOKIE, ValCommit, ValDeployment, createValClient, remirrorToRichTextSource, richTextToRemirror, urlOf };
